import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  cn,
  Divider,
  Spinner,
  Stack,
} from 'component-library';
import { Dispatch, SetStateAction, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';

import { RoadmapNode } from '@/api/rest/resources/types/organization';
import { Workstream, WORKSTREAM_MILESTONE_STATUS } from '@/api/rest/resources/types/workstreams';
import { SuspenseBoundary } from '@/components/SuspenseBoundary';
import { ProgressCircle } from '@/components/widgets/a/Progress/ProgressCircle';
import { Card } from '@/components/widgets/o/Card/Card';
import { useOrganizationWorkstream } from '@/hooks/useOrganizationWorkstream';
import { Logger } from '@/lib/logs/logger';
import { useWorkstreamId } from '@/pages/shared/home/hooks/useWorkstreamId';
import {
  getMilestoneProgress,
  getRoadmapFromWorkstream,
  getWorkstreamMilestones,
  getWorkstreamMilestoneStatus,
} from '@/pages/shared/home/utils/index';
import { useOrganizationRoadmapNodes } from '@/pages/shared/hooks/useOrganizationRoadmapNodes';

type WorkstreamOverviewProps = {
  styles?: Record<string, string>;
};

export const WorkstreamOverview = (props?: WorkstreamOverviewProps) => {
  const workstreamId = useWorkstreamId();
  const { t } = useTranslation();

  if (!workstreamId) {
    return null;
  }

  return (
    <ErrorBoundary
      FallbackComponent={() => (
        <Card
          styles={{
            root: 'rounded-3xl p-4',
          }}
        >
          <Stack spacing={4}>
            <span className='typography-body1'>{t('global.workstream.labels.workstreamOverview')}</span>
            <span className='typography-body1 text-text-disabled'>{t('global.widget.error.message')}</span>
          </Stack>
        </Card>
      )}
      onError={(error, info) => {
        Logger.error(`Workstream overview rendering error: ${error} ${info}`);
      }}
    >
      <SuspenseBoundary
        fallback={
          <Stack centerMain>
            <Spinner size={10} />
          </Stack>
        }
      >
        <WorkstreamOverviewBase id={workstreamId} {...props} />
      </SuspenseBoundary>
    </ErrorBoundary>
  );
};

const WorkstreamOverviewBase = ({ id, ...props }: { id: string } & WorkstreamOverviewProps) => {
  const { t } = useTranslation();
  const workstream = useOrganizationWorkstream(id).data;

  const roadmap = getRoadmapFromWorkstream(workstream);
  const milestones = roadmap?.children ?? [];
  const validatedMilestones = milestones.filter((m) => !!m.id);

  const defaultSelectedMilestoneId = validatedMilestones.find(
    (m) => getWorkstreamMilestoneStatus(m.status) === WORKSTREAM_MILESTONE_STATUS.IN_PROGRESS,
  )?.id;
  const defaultSelectedMilestone = useOrganizationRoadmapNodes(undefined, {
    roadmap_node_id: defaultSelectedMilestoneId,
  }).data.at(0);

  const [selectedMilestone, setSelectedMilestone] = useState<RoadmapNode | null>(defaultSelectedMilestone ?? null);

  if (!workstream) {
    return null;
  }

  return (
    <Card
      styles={{
        root: cn('rounded-3xl p-0 w-full', props?.styles?.root ?? ''),
        body: cn('p-0', props?.styles?.body ?? ''),
      }}
    >
      <Accordion type='single' collapsible>
        <AccordionItem value={id} className='rounded-3xl bg-white-100'>
          <AccordionTrigger className='p-4 items-start' data-testid='workstream-overview-trigger'>
            <Stack spacing={3} className='w-full'>
              <Stack direction='row' spacing={3} className='items-center'>
                <div className='flex h-10 w-10 items-center justify-center rounded-lg bg-white-100'>
                  <i className='ri-compass-discover-line text-xl text-primary-100' />
                </div>
                <Stack spacing={0.5} className='items-start'>
                  <span className='typography-overline text-text-secondary uppercase'>
                    {t('global.workstream.labels.workstreamProgress')}
                  </span>
                  <span className='typography-h3 text-text-primary'>{workstream.title}</span>
                </Stack>
              </Stack>
              <MilestoneProgressBar workstream={workstream} />
            </Stack>
          </AccordionTrigger>
          <AccordionContent>
            {validatedMilestones.length > 0 ? (
              <Stack direction='row' className='p-0 pt-4 gap-6'>
                <Stack spacing={1} className='w-2/3'>
                  {validatedMilestones.map((milestone, index) => (
                    <MilestoneRow
                      key={milestone.id ?? index}
                      milestoneId={milestone.id ?? ''}
                      index={index}
                      setSelectedMilestone={setSelectedMilestone}
                      isLast={index === validatedMilestones.length - 1}
                      isSelected={selectedMilestone?.id === milestone.id}
                    />
                  ))}
                </Stack>
                {selectedMilestone && <MilestoneNextSteps milestone={selectedMilestone} />}
              </Stack>
            ) : (
              <Stack className='w-full justify-start items-start flex-row text-text-secondary typography-body1 pl-3'>
                {t('global.workstream.labels.noMilestonesHint')}
              </Stack>
            )}
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </Card>
  );
};

const MilestoneProgressBar = ({ workstream }: { workstream: Workstream }) => {
  const { completed, total } = getWorkstreamMilestones(workstream);
  if (total === 0) {
    return null;
  }
  return (
    <Stack direction='row' spacing={1} className='items-center w-full'>
      {Array.from({ length: total }).map((_, index) => {
        const color = index < completed ? 'primary-100' : 'neutral-black-12';
        const bgColor = index < completed - 1 ? `bg-${color}` : 'bg-transparent';

        return (
          <Stack direction='row' key={`milestone-bar-${index}`} centerMain center className='flex-1 h-4' spacing={1}>
            <div className={`grow h-2 rounded-full bg-${color}`} />
            <div
              className={`h-4 w-4 rounded-full border-2 border-${color} ${index < total - 1 ? 'opacity-100' : 'opacity-0'} ${bgColor}`}
            />
          </Stack>
        );
      })}
    </Stack>
  );
};

type MilestoneRowProps = {
  milestoneId: string;
  index: number;
  setSelectedMilestone: Dispatch<SetStateAction<RoadmapNode | null>>;
  isLast: boolean;
  isSelected: boolean;
};

const MilestoneRow = ({ milestoneId, index, setSelectedMilestone, isLast, isSelected }: MilestoneRowProps) => {
  const milestone = useOrganizationRoadmapNodes(undefined, { roadmap_node_id: milestoneId }).data.at(0);

  if (!milestone) {
    return null;
  }

  const handleSelectMilestone = () => {
    setSelectedMilestone(milestone);
  };

  const progressValue = getMilestoneProgress(milestone);

  return (
    <Stack spacing={1} className='px-6 pb-2'>
      <Stack
        direction='row'
        onClick={handleSelectMilestone}
        className={`w-full justify-between hover:bg-secondary-30 p-1 rounded-lg cursor-pointer ${isSelected ? 'bg-neutral-black-5' : ''}`}
        center
        centerMain
        spacing={1}
      >
        <Stack spacing={2}>
          <span className='typography-overline text-text-secondary uppercase'>
            {index + 1}. {milestone.title}
          </span>
          <span className='typography-body2'>{milestone.description}</span>
        </Stack>

        <Stack direction='row' spacing={2} className='items-center'>
          <span className='typography-body2 font-medium text-text-primary'>{progressValue}%</span>
          <ProgressCircle value={progressValue} />
        </Stack>
      </Stack>
      {!isLast && <Divider className='w-full' />}
    </Stack>
  );
};

type MilestoneNextStepsProps = {
  milestone: RoadmapNode;
};

const MilestoneNextSteps = ({ milestone }: MilestoneNextStepsProps) => {
  const { t } = useTranslation();

  const nextSteps = milestone.children?.filter((child) => child.id && child.visible_to_customer) ?? [];

  return (
    <Stack spacing={3} className='flex-1'>
      <span className='typography-overline text-text-secondary uppercase'>
        {t('global.workstream.labels.nextSteps')}
      </span>
      <Stack spacing={3}>
        {nextSteps.map((step, index) => (
          <MilsteoneStep key={`milestone-step-${index}`} title={step.title} status={step.status} />
        ))}
        {nextSteps.length === 0 && (
          <span className='typography-body2 text-text-disabled'>{t('global.workstream.labels.noNextSteps')}</span>
        )}
      </Stack>
    </Stack>
  );
};

type MilsteoneStepsProps = {
  title: string;
  status: RoadmapNode['status'];
};

const MilsteoneStep = ({ title, status }: MilsteoneStepsProps) => {
  const milestoneStatus = getWorkstreamMilestoneStatus(status);

  return (
    <Stack direction='row' spacing={3} className='items-center'>
      <div
        className={`h-4 w-4 shrink-0 rounded-full border-2 ${milestoneStatus === WORKSTREAM_MILESTONE_STATUS.COMPLETE ? 'border-green-500' : 'border-neutral-black-12'} bg-white-100`}
      />
      <span className='typography-body2 text-text-primary'>{title}</span>
    </Stack>
  );
};
